/**
 * EVENTSFLO-ALIGN-INBOX-DOCUMENT-VIEWER-WITH-AI-ONBOARDING-V1
 *
 * Shared PDF viewer with pagination, zoom, and click-and-drag pan — the same
 * behaviour the AI Crew Onboarding screen ships. Built on react-pdf (PDF.js)
 * so it works without browser-native PDF plugin support.
 *
 * Reused by:
 *  - DocumentReviewWorkspaceV2 (Inbox attachment review)
 *  - (future) AICrewOnboardingPage
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Loader2, FileWarning, Eye, Printer } from "lucide-react";
import { Button } from "../ui/button";

// Match the worker version with the bundled pdfjs-dist version.
if (typeof pdfjs !== "undefined" && pdfjs?.GlobalWorkerOptions) {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

export default function PdfPanZoomViewer({ src, fileName, height = "70vh" }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loadError, setLoadError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  // EVENTSFLO-INBOX-PDF-PAN-AND-FIT-VIEW-V1: track scroll-area size + page intrinsics
  const [containerHeight, setContainerHeight] = useState(0);
  const [pageNatural, setPageNatural] = useState({ width: 0, height: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const containerRef = useRef(null);
  const panStateRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const options = useMemo(
    () => ({
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
    }),
    []
  );

  const onLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setPageNumber(1);
    setLoadError(null);
  }, []);

  const onLoadError = useCallback((err) => {
    console.error("[PdfPanZoomViewer] load error:", err);
    setLoadError(err?.message || "Failed to load PDF");
  }, []);

  const measureRef = useCallback((node) => {
    containerRef.current = node;
    if (node) {
      setContainerWidth(node.clientWidth);
      setContainerHeight(node.clientHeight);
    }
  }, []);

  // EVENTSFLO-INBOX-PDF-PAN-AND-FIT-VIEW-V1: re-measure on resize so Fit Page
  // recomputes correctly when the modal/viewport changes.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      setContainerWidth(node.clientWidth);
      setContainerHeight(node.clientHeight);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // EVENTSFLO-INBOX-PDF-PAN-AND-FIT-VIEW-V1: apply Fit Page automatically the
  // first time we know the page intrinsic size + container size. Subsequent
  // user zoom actions are preserved.
  const didAutoFitRef = useRef(false);
  useEffect(() => {
    if (didAutoFitRef.current) return;
    if (!pageNatural.width || !pageNatural.height) return;
    if (!containerWidth || !containerHeight) return;
    const fitW = (containerWidth - 16) / pageNatural.width;
    const fitH = (containerHeight - 16) / pageNatural.height;
    const next = Math.max(0.25, Math.min(3, Math.min(fitW, fitH)));
    setScale(+next.toFixed(2));
    didAutoFitRef.current = true;
  }, [pageNatural, containerWidth, containerHeight]);

  const handleFitPage = useCallback(() => {
    const w = containerWidth;
    const h = containerHeight;
    if (!pageNatural.width || !pageNatural.height || !w || !h) return;
    const fitWidth = (w - 16) / pageNatural.width;
    const fitHeight = (h - 16) / pageNatural.height;
    const next = Math.max(0.25, Math.min(3, Math.min(fitWidth, fitHeight)));
    setScale(+next.toFixed(2));
  }, [containerWidth, containerHeight, pageNatural]);

  const handleFitWidth = useCallback(() => {
    setScale(1.0);
  }, []);

  // EVENTSFLO-DOCUMENT-VIEWER-PRINT-ACTION-V1: browser-native print via hidden iframe
  // Pointing at the original file URL lets the browser render the full PDF
  // (or image) in its own print pipeline, rather than us rasterising pages.
  const handlePrint = useCallback(() => {
    if (!src) return;
    try {
      const existing = document.getElementById("eventsflo-print-frame");
      if (existing) existing.remove();
      const iframe = document.createElement("iframe");
      iframe.id = "eventsflo-print-frame";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      iframe.src = src;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (err) {
          // Fallback: open in new tab so user can print from there
          window.open(src, "_blank", "noopener,noreferrer");
        }
      };
      document.body.appendChild(iframe);
    } catch (err) {
      window.open(src, "_blank", "noopener,noreferrer");
    }
  }, [src]);

  const handlePanStart = useCallback((e) => {
    const node = containerRef.current;
    if (!node) return;
    const canPan =
      node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight;
    if (!canPan) return;
    panStateRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: node.scrollLeft,
      scrollTop: node.scrollTop,
    };
    setIsPanning(true);
  }, []);

  const handlePanMove = useCallback((e) => {
    const state = panStateRef.current;
    const node = containerRef.current;
    if (!state.dragging || !node) return;
    e.preventDefault();
    node.scrollLeft = state.scrollLeft - (e.clientX - state.startX);
    node.scrollTop = state.scrollTop - (e.clientY - state.startY);
  }, []);

  const handlePanEnd = useCallback(() => {
    if (panStateRef.current.dragging) {
      panStateRef.current.dragging = false;
      setIsPanning(false);
    }
  }, []);

  return (
    <div
      className="w-full flex flex-col"
      data-testid="pdf-pan-zoom-viewer"
      style={{ height, minHeight: "400px" }}
    >
      {/* EVENTSFLO-INBOX-BATCH-HIDE-AND-PDF-VIEWER-CONTROLS-V1:
          toolbar pinned at the TOP so zoom + pagination stay reachable
          when viewing tall portrait PDFs that overflow the scroll area. */}
      <div className="sticky top-0 z-10 p-2 border-b bg-zinc-900/90 backdrop-blur flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={!numPages || pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            data-testid="pdf-pan-zoom-prev"
          >
            Prev
          </Button>
          <span className="text-xs text-muted-foreground px-2 min-w-[80px] text-center">
            {numPages ? `${pageNumber} / ${numPages}` : "—"}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!numPages || pageNumber >= numPages}
            onClick={() =>
              setPageNumber((p) => Math.min(numPages || 1, p + 1))
            }
            data-testid="pdf-pan-zoom-next"
          >
            Next
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)))
            }
            disabled={!numPages}
            data-testid="pdf-pan-zoom-out"
          >
            −
          </Button>
          <span className="text-xs text-muted-foreground px-2 min-w-[48px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))
            }
            disabled={!numPages}
            data-testid="pdf-pan-zoom-in"
          >
            +
          </Button>
          {/* EVENTSFLO-INBOX-PDF-PAN-AND-FIT-VIEW-V1 */}
          <div className="w-px h-4 bg-white/10 mx-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={handleFitPage}
            disabled={!numPages || !pageNatural.width}
            data-testid="pdf-pan-zoom-fit-page"
            title="Fit whole page"
          >
            Fit Page
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleFitWidth}
            disabled={!numPages}
            data-testid="pdf-pan-zoom-fit-width"
            title="Fit width"
          >
            Fit Width
          </Button>
          {/* EVENTSFLO-DOCUMENT-VIEWER-PRINT-ACTION-V1 */}
          <div className="w-px h-4 bg-white/10 mx-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={!src}
            data-testid="pdf-pan-zoom-print"
            title="Print PDF"
          >
            <Printer className="h-3.5 w-3.5 mr-1" />
            Print
          </Button>
        </div>

        {fileName && (
          <span
            className="text-xs text-muted-foreground truncate max-w-[200px]"
            title={fileName}
          >
            {fileName}
          </span>
        )}
      </div>

      <div
        ref={measureRef}
        className="w-full bg-muted/20 overflow-auto flex-1"
        style={{
          cursor: isPanning ? "grabbing" : scale > 1 ? "grab" : "default",
        }}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
      >
        {loadError ? (
          <div className="p-8 text-center max-w-md mx-auto">
            <FileWarning className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium mb-1">Could not render PDF inline</p>
            <p className="text-xs text-muted-foreground mb-3">{loadError}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
              data-testid="pdf-pan-zoom-open-newtab"
            >
              <Eye className="h-4 w-4 mr-1" />
              Open in new tab
            </Button>
          </div>
        ) : (
          <div
            className="mx-auto select-none"
            style={{ width: "fit-content", padding: "8px" }}
            onDragStart={(e) => e.preventDefault()}
          >
            <Document
              file={src}
              onLoadSuccess={onLoadSuccess}
              onLoadError={onLoadError}
              options={options}
              loading={
                <div className="p-8 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading PDF…
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                width={
                  containerWidth
                    ? Math.max(containerWidth - 16, 320) * scale
                    : undefined
                }
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onLoadSuccess={(p) => {
                  // EVENTSFLO-INBOX-PDF-PAN-AND-FIT-VIEW-V1: capture intrinsic
                  // page size (CSS pixels @ scale=1) so Fit Page can compute.
                  const w = p?.originalWidth || p?.width;
                  const h = p?.originalHeight || p?.height;
                  if (w && h) setPageNatural({ width: w, height: h });
                }}
                data-testid={`pdf-pan-zoom-page-${pageNumber}`}
              />
            </Document>
          </div>
        )}
      </div>
    </div>
  );
}
